import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Dropdown from 'app/components/Dropdown';
import FilterDropdown, { type SelectOption } from 'app/components/FilterDropdown';
import type { MonitoringFilter } from 'app/pages/MonitoringPage/MonitoringFilterContext/types';
import {
  COMPETITORS_LOGO,
  COMPETITORS_NAME,
  MONITORING_MARKETS
} from 'app/config/competitors.config';
import Icon from 'app/components/Icon';
import Checkbox from 'app/components/Checkbox';
import { toggleValueInArray } from 'app/utils/array';
import InputNumber from 'app/components/Input/InputNumber';
import { parseFloatOrZero } from 'app/utils/numbers';
import Button from 'app/components/Button';
import { initialState } from 'app/pages/MonitoringPage/MonitoringFilterContext/reducer';
import FilterTag from 'app/components/FilterTag';
import Tooltip from 'app/components/Tooltip';
import { getMonitoringCompetitors } from 'app/config/competitors.config.helper';

import style from './style.scss';

interface Props {
  values: MonitoringFilter;
  onValueChange: (key: keyof MonitoringFilter, value: any) => void;
  resetFilters: () => void;
  numberOfHiddenMarkets: number | undefined;
}

// Contant value
const COMPETITORS_TO_DISPLAY = getMonitoringCompetitors();

const MonitoringFilters: FC<Props> = ({
  values,
  onValueChange,
  resetFilters,
  numberOfHiddenMarkets
}) => {
  const { t } = useTranslation();

  const [scoreFilter, setScoreFilter] = useState(values.scoreThreshold.toFixed(1));
  const [scoreFilterModalOpened, setScoreFilterModalOpened] = useState(false);

  const marketNames = t('market', { returnObjects: true }) as Record<string, string>;

  const marketOptions: SelectOption<(typeof MONITORING_MARKETS)[0]>[] = MONITORING_MARKETS.map(
    market => {
      const [template, type] = market.split('__');
      const translated = marketNames?.[template].replaceAll('Over/Under', 'O/U') || template;
      return { label: [translated, type].join(', '), value: market };
    }
  );

  const toggleCompetitorComparison = (slug: CompetitorSlug) => {
    const newValues = toggleValueInArray(values.comparingWith, slug);
    onValueChange('comparingWith', newValues);
  };

  return (
    <>
      <div className={style.monitoringFilters}>
        <div>
          <FilterDropdown
            label={t('monitoring-filters-market')}
            options={marketOptions}
            value={values.markets}
            multi
            onChange={value => onValueChange('markets', value)}
            clearLabel={t('events-filter-reset_filters')}
            width={250}
            itemClassName={style.marketOption}
          />
          <Dropdown
            withArrow
            label={t('monitoring-filters-score')}
            variation="empty"
            className={style.filterButton}
            dropdownClassName={style.scoreModal}
            onOpenChange={becomeOpened => {
              setScoreFilterModalOpened(becomeOpened);
              if (!becomeOpened) {
                onValueChange('scoreThreshold', parseFloatOrZero(scoreFilter));
              } else {
                setScoreFilter(values.scoreThreshold.toFixed(1));
              }
            }}
            opened={scoreFilterModalOpened}
          >
            <div className={style.scoreModalTitle}>{t('monitoring-filters-score_modal-title')}</div>
            <div className={style.scoreModalDescription}>
              {t('monitoring-filters-score_modal-description')}
            </div>
            <InputNumber
              value={scoreFilter}
              onChange={value => setScoreFilter(value)}
              className={style.scoreModalInput}
              suffix="%"
              containerClassName={style.scoreModalInputContainer}
            />
          </Dropdown>

          <Checkbox
            status={values.outsideOfMarket ? 'checked' : 'unchecked'}
            label={t('monitoring-filters-outside_of_market')}
            size="medium"
            onChange={() => onValueChange('outsideOfMarket', !values.outsideOfMarket)}
            labelPosition="right"
          />

          <Button
            className={classNames(style.filterButton, style.withHover)}
            variation="empty"
            onClick={() => onValueChange('showHidden', !values.showHidden)}
          >
            {values.showHidden ? t('monitoring-filters-back') : t('monitoring-filters-show_hidden')}
            {!values.showHidden && numberOfHiddenMarkets !== undefined
              ? ` (${numberOfHiddenMarkets})`
              : ''}
          </Button>
        </div>

        <div>
          <div className={style.comparing}>{t('monitoring-filters-compare')}</div>
          {COMPETITORS_TO_DISPLAY.filter(slug => !slug.includes('pandaodds')).map(slug => {
            const isSelected = values.comparingWith.includes(slug);
            const isDisabled = values.comparingWith.length === 1 && isSelected;

            return (
              <div key={slug} className={style.competitor}>
                <Tooltip
                  active={isDisabled}
                  content={t('monitoring-filters-compare-tooltip')}
                  className={style.competitorTooltip}
                >
                  <Checkbox
                    status={isSelected ? 'checked' : 'unchecked'}
                    onChange={() => toggleCompetitorComparison(slug)}
                    size="medium"
                    disabled={isDisabled}
                    labelPosition="right"
                    labelClassName={style.competitorLabel}
                    className={style.competitorLabel}
                    label={
                      <>
                        <Icon href={COMPETITORS_LOGO[slug]} className={style.competitorLogo} />
                        {COMPETITORS_NAME[slug]}
                      </>
                    }
                  />
                </Tooltip>
              </div>
            );
          })}
        </div>
      </div>
      {(values.markets.length || values.scoreThreshold !== initialState.filters.scoreThreshold) && (
        <div className={style.filterTags}>
          {values.markets.map(market => {
            const value = marketOptions.find(option => option.value === market)?.label;
            if (!value) return null;
            return (
              <FilterTag
                key={market}
                onDelete={() =>
                  onValueChange('markets', toggleValueInArray(values.markets, market))
                }
              >
                {value}
              </FilterTag>
            );
          })}
          {values.scoreThreshold !== initialState.filters.scoreThreshold && (
            <FilterTag
              onDelete={() => onValueChange('scoreThreshold', initialState.filters.scoreThreshold)}
            >
              {t('monitoring-filters-score-tag', { score: values.scoreThreshold.toFixed(1) })}
            </FilterTag>
          )}

          <Button variation="empty" onClick={resetFilters}>
            {t('events-filter-reset_filters')}
          </Button>
        </div>
      )}
    </>
  );
};

MonitoringFilters.displayName = 'MonitoringFilters';

export default MonitoringFilters;
